import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import EmptyState from '../shared/EmptyState';
import SeeAllLink from '../shared/SeeAllLink';
import CardGroup from './CardGroup';
import HomeSectionHeader from './HomeSectionHeader';
import { FeedCardSkeleton } from './DashboardSkeleton';
import { renderFeedCard } from './renderFeedCard';
import { isDismissed } from '../../utils/composeWhatsOnSections';
import { feedItemKey } from '../../utils/feedItemKey';

const CARD_LIMIT = 4;
// Places and the curated things to do at them. Posts (trips, albums,
// collections, boards) live in Travel inspiration now.
const PLACE_TYPES = new Set(['place', 'walking_tour', 'tourism_experience', 'viator_experience', 'tiqets_experience']);
const CARD_IMAGE_SIZING = { surface: 'medium', sizes: '(max-width: 640px) 45vw, 288px' };
const CARD_SLOT = 'h-full min-w-0';

/**
 * "New places to explore around Antwerp": undated places, walks and tours
 * near home that aren't on the viewer's lists yet (What's Around, filtered to
 * places and to what they haven't saved). No dates, no rush — anything with
 * a date you could miss belongs to This weekend (docs/DISCOVERY_LISTS_MODEL.md §3).
 */
export default function NewPlacesSection({ data, homeCity, buildActions, modals, dismissedIds, suppressKeys, onRetry, sectionRef, attribution }) {
  const { t } = useTranslation('home');

  const cards = useMemo(() => {
    let items = (data.items || []).filter(i => PLACE_TYPES.has(i.content_type) && !i.user_saved);
    if (dismissedIds?.size > 0) items = items.filter(item => !isDismissed(item, dismissedIds));
    if (suppressKeys?.size > 0) {
      items = items.filter(item => {
        const key = feedItemKey(item);
        return key === null || !suppressKeys.has(key);
      });
    }
    return items.slice(0, CARD_LIMIT);
  }, [data.items, dismissedIds, suppressKeys]);

  const loading = (data.loading || !data.loaded) && !data.error;
  if (!loading && !data.error && cards.length === 0) return null;

  return (
    <section ref={sectionRef} aria-labelledby="home-places-heading" className="scroll-mt-20">
      <HomeSectionHeader
        id="home-places-heading"
        title={homeCity ? t('places.title', { city: homeCity }) : t('places.titleNoCity')}
        subtitle={t('places.subtitle')}
        action={!loading && cards.length > 0 && <SeeAllLink to="/social-feed?types=places%2Ctours&where=home&km=25">{t('places.seeAll')}</SeeAllLink>}
      />
      {loading ? (
        <CardGroup cols={4} phone="grid2">
          <span className="sr-only" role="status">{t('dashboardSkeleton.loadingFeed')}</span>
          {Array.from({ length: 4 }).map((_, i) => <FeedCardSkeleton key={i} />)}
        </CardGroup>
      ) : data.error ? (
        <EmptyState.Error message={typeof data.error === 'string' ? data.error : undefined} onRetry={onRetry} />
      ) : (
        <>
          <CardGroup cols={4} phone="grid2" className="animate-fade-in">
            {cards.map((item, index) => renderFeedCard(item, index, { buildActions, modals, imageSizing: CARD_IMAGE_SIZING, slotClassName: CARD_SLOT }))}
          </CardGroup>
          {attribution}
        </>
      )}
    </section>
  );
}
